// src/components/legal/CryptCrawlerTermsOfService.js
import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const Container = styled.div`
    max-width: 800px;
    margin: 0 auto;
    padding: 3rem 2rem;
    line-height: 1.8;
    color: #333;
`;

const Header = styled.header`
    text-align: center;
    margin-bottom: 3rem;
    padding-bottom: 2rem;
    border-bottom: 2px solid #eee;
`;

const Title = styled.h1`
    font-size: 2.5rem;
    margin-bottom: 1rem;
    color: #9B2C2C;
`;

const UpdateDate = styled.p`
    color: #666;
    font-size: 1rem;
`;

const BackLink = styled(Link)`
    display: inline-flex;
    align-items: center;
    color: #9B2C2C;
    text-decoration: none;
    margin-bottom: 2rem;
    transition: opacity 0.3s;

    &:hover {
        opacity: 0.8;
    }

    &::before {
        content: '←';
        margin-right: 0.5rem;
    }
`;

const Section = styled.section`
    margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: #444;
`;

const Paragraph = styled.p`
    margin-bottom: 1rem;
    text-align: justify;
    white-space: pre-wrap;
`;

const WarningBox = styled.div`
    background: #FFF3E0;
    border-left: 4px solid #FF9800;
    padding: 1.5rem;
    border-radius: 5px;
    margin: 2rem 0;
`;

const InfoBox = styled.div`
    background: #FBEDED;
    padding: 1.5rem;
    border-radius: 10px;
    margin: 2rem 0;
    white-space: pre-wrap;
`;

const CryptCrawlerTermsOfService = () => {
    return (
        <Container>
            <BackLink to="/cryptcrawler">CryptCrawler 홈으로 / Back to CryptCrawler</BackLink>

            <Header>
                <Title>CryptCrawler Terms of Service / 이용약관</Title>
                <UpdateDate>Last updated / 최종 수정일: October 13, 2026</UpdateDate>
            </Header>

            <WarningBox>
                <strong>Notice / 안내:</strong> CryptCrawler is a single-player roguelike deck-building dungeon crawler intended for entertainment. By installing or using the game, you agree to these Terms. If you do not agree, please uninstall the game and do not use the service.
                <br /><br />
                CryptCrawler는 오락 목적의 싱글 플레이 로그라이크 덱빌딩 던전 게임입니다. 본 약관에 동의하지 않으시면 게임을 이용하실 수 없습니다.
            </WarningBox>

            <Section>
                <SectionTitle>1. Acceptance of Terms / 약관 동의</SectionTitle>
                <Paragraph>
                    These Terms of Service ("Terms") govern your access to and use of the CryptCrawler mobile game ("Service") provided by MindWorld ("Company", "we", "us"). By installing, accessing, or using the Service, you confirm that:

                    • You have read, understood, and agreed to these Terms and our Privacy Policy.
                    • You are at least 13 years of age (16 in EEA jurisdictions that apply the higher age threshold under GDPR Art. 8).
                    • You have the legal capacity to enter into a binding agreement, or have obtained consent from a parent or legal guardian.
                    • You will comply with all applicable laws and regulations.

                    본 약관에 동의함으로써 만 13세 이상(EU 일부 국가는 16세)이며 법적 계약 체결 능력이 있음을 확인합니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>2. The Service / 서비스 설명</SectionTitle>
                <Paragraph>
                    CryptCrawler is a free-to-start single-player game offered on mobile platforms. The game includes:

                    • Dungeon exploration and turn-based card battles across multiple acts and floors
                    • Playable classes, card collection, and deck building within each run
                    • Permanent progression (unlocks, card mastery, achievements) and a permanent in-game currency (platinum), plus in-run gold
                    • Optional rewarded video advertisements that grant in-game rewards, and interstitial advertisements
                    • Optional in-app purchases, including the full version, additional content (classes, acts), ad removal, battle speed options, and platinum packs
                    • Local save of game progress on your device (no account, no cloud save)

                    The Service is provided "as is" and may change, expand, or be discontinued at any time.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>3. License Grant / 라이선스 부여</SectionTitle>
                <Paragraph>
                    Subject to your compliance with these Terms, we grant you a personal, non-exclusive, non-transferable, revocable, limited license to install and use CryptCrawler on a compatible device that you own or control, solely for your personal, non-commercial entertainment.

                    All rights not expressly granted to you are reserved by us.

                    본 약관 준수 조건 하에 개인적, 비상업적 용도로만 게임을 이용할 수 있는 제한된 라이선스를 부여합니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>4. User Conduct / 이용자 의무</SectionTitle>
                <Paragraph>
                    You agree NOT to:

                    • Reverse engineer, decompile, disassemble, or attempt to derive source code
                    • Modify, hack, cheat, or exploit the game (including memory editors, save editing, automation, modded clients)
                    • Circumvent advertising or in-app purchase mechanisms, or obtain paid content without paying
                    • Use the Service for any unlawful, fraudulent, or harmful purpose
                    • Distribute malware or any code that interferes with the Service
                    • Resell, rent, or sublicense the Service or any part of it
                    • Violate intellectual property rights of others

                    Violations may result in termination of your license without refund.

                    위반 시 사전 통지 없이 서비스 이용이 제한될 수 있으며, 이 경우 환불되지 않습니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>5. In-App Purchases / 인앱 구매</SectionTitle>
                <Paragraph>
                    CryptCrawler offers optional in-app purchases. All purchases are processed by the platform store (Google Play Billing / Apple App Store) under their terms of service.

                    Product types:
                    • Non-consumable (permanent): full version, class and act unlocks, ad removal, battle speed options. These can be restored on the same store account via "Restore Purchases" or by reinstalling.
                    • Consumable: platinum packs. Platinum is credited to the save on your device and cannot be restored after it is spent, or if local data is deleted.

                    Pricing: Displayed in your local currency, inclusive of applicable taxes. We may change prices with prospective effect.

                    Virtual items: Platinum, gold, and other virtual items have no monetary value, cannot be exchanged for cash or transferred, and exist only within the game. Because game data is stored only on your device, uninstalling the app or clearing app data permanently deletes unspent virtual currency and progress; we cannot recover it.

                    Refunds: Subject to the platform store's refund policy. Korean users have additional rights under the Act on Consumer Protection in Electronic Commerce (전자상거래법 제17조) — you may request a withdrawal within 7 days of purchase if the digital content has not been used. For EU users, the statutory 14-day right of withdrawal applies, but is waived for digital content delivered immediately upon your consent (consent obtained at purchase).

                    Contact mind-wellness@naver.com for refund disputes.

                    인앱 구매: 영구 상품(정식판·직업/막 해금·광고 제거·전투 배속)은 같은 스토어 계정으로 복원 가능하며, 소모성 상품(백금 팩)은 기기에 저장되어 사용 후 또는 앱 데이터 삭제 시 복원되지 않습니다. 환불은 플랫폼 스토어 정책 및 전자상거래법·EU 소비자권리지침에 따릅니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>6. Advertisements / 광고</SectionTitle>
                <Paragraph>
                    CryptCrawler is supported by advertisements served via Google AdMob. The game offers optional rewarded video ads that you choose to watch in exchange for in-game rewards, and may show interstitial ads. Purchasing ad removal or the full version removes non-rewarded ads.

                    Ad personalization is governed by your consent (in EEA/UK/Switzerland) or your device's advertising preferences (in other regions).

                    We are not responsible for the content of third-party advertisements or websites linked from them.

                    광고: 게임은 Google AdMob 광고로 운영되며, 광고 콘텐츠는 제3자가 제공하므로 회사는 그에 대해 책임지지 않습니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>7. Save Data / 저장 데이터</SectionTitle>
                <Paragraph>
                    Your progress is stored only on your device. The game keeps automatic local backups to recover a damaged save, but we do not provide cloud backup and cannot restore lost progress (for example after uninstalling, clearing app data, changing devices, or device failure). Game balance and content may change in updates, which may affect existing saves.

                    저장 데이터는 기기에만 저장되며, 회사는 클라우드 백업을 제공하지 않으므로 앱 삭제·데이터 삭제·기기 변경·기기 고장 시 진행 상황을 복구할 수 없습니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>8. Intellectual Property / 지적재산권</SectionTitle>
                <Paragraph>
                    All content in CryptCrawler, including but not limited to graphics, sound, music, code, logos, card designs, and the "CryptCrawler" name, is the property of MindWorld or its licensors, and is protected by copyright, trademark, and other intellectual property laws.

                    You may not copy, modify, distribute, sell, or create derivative works of the Service without our prior written consent. Sharing screenshots and gameplay videos for non-commercial purposes is permitted.

                    게임의 모든 콘텐츠는 회사 또는 라이선스 제공자의 자산이며, 무단 복제·배포·수정은 금지됩니다. 비상업적 목적의 스크린샷·플레이 영상 공유는 허용됩니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>9. Third-Party Services / 제3자 서비스</SectionTitle>
                <Paragraph>
                    The Service integrates with third-party services and libraries, including:

                    • Google AdMob (advertising)
                    • Unity In-App Purchasing and Google Play Billing / Apple App Store (purchases)
                    • Unity engine and other licensed asset and open-source libraries (under their respective licenses)

                    Your use of these third-party services is governed by their own terms and privacy policies. We are not responsible for third-party services.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>10. Disclaimers / 면책조항</SectionTitle>
                <Paragraph>
                    TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR UNINTERRUPTED OPERATION.

                    We do not warrant that the Service will be error-free or available at all times. Use of the Service is at your own risk.

                    Nothing in these Terms excludes or limits liability that cannot be excluded under applicable law.

                    법이 허용하는 최대 범위 내에서 서비스는 "있는 그대로" 제공되며, 명시적·묵시적 어떠한 보증도 하지 않습니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>11. Limitation of Liability / 책임 제한</SectionTitle>
                <Paragraph>
                    To the maximum extent permitted by applicable law, in no event will MindWorld be liable for:

                    • Any indirect, incidental, special, consequential, exemplary, or punitive damages
                    • Loss of profits, data (including save data), goodwill, or business opportunity
                    • Damages exceeding the greater of (a) amounts you paid to us in the 12 months preceding the claim, or (b) USD 50

                    For consumers in the EEA and Korea, mandatory consumer protections are unaffected.

                    책임 한도: 법이 허용하는 최대 범위 내에서 책임이 제한되며, EU/한국 소비자 보호 강행규정은 영향받지 않습니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>12. Indemnification / 손해배상</SectionTitle>
                <Paragraph>
                    You agree to indemnify and hold harmless MindWorld and its affiliates, officers, agents, and employees from any claim, demand, loss, or expense (including reasonable attorneys' fees) arising out of your use of the Service, your breach of these Terms, or your violation of any law or rights of a third party.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>13. Termination / 서비스 해지</SectionTitle>
                <Paragraph>
                    By you: Uninstall the game at any time. See the Data Deletion page for how to delete data.

                    By us: We may terminate your license for any breach of these Terms, fraud, or where required by law. We may also discontinue the Service in whole or in part, with reasonable notice where practicable.

                    Effects of termination: Your right to use the Service ceases. Sections relating to intellectual property, disclaimers, limitation of liability, indemnification, and governing law survive termination.

                    해지: 사용자는 언제든 게임을 삭제할 수 있으며, 회사는 약관 위반 시 이용을 제한할 수 있습니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>14. Governing Law and Disputes / 준거법 및 분쟁 해결</SectionTitle>
                <Paragraph>
                    Governing law: These Terms are governed by the laws of the Republic of Korea, without regard to its conflict of law principles.

                    Consumer protections: If you are a consumer residing in the EEA, UK, or another jurisdiction with mandatory consumer protection laws, you also benefit from the mandatory provisions of the law of your country of residence.

                    Dispute resolution:
                    1. Informal resolution: contact mind-wellness@naver.com first.
                    2. Mediation: Korean Consumer Agency (소비자분쟁조정위원회) for Korean consumers.
                    3. Litigation: Seoul Central District Court, Republic of Korea, has exclusive jurisdiction unless mandatory consumer law provides otherwise.

                    준거법: 대한민국 법률. 한국 소비자는 소비자분쟁조정위원회 조정 절차를 이용할 수 있습니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>15. Changes to These Terms / 약관 변경</SectionTitle>
                <Paragraph>
                    We may modify these Terms from time to time. Material changes will be announced in-app or on this page at least 30 days before they take effect (7 days for non-material changes). Your continued use of the Service after the effective date constitutes acceptance of the modified Terms. If you do not agree, please uninstall the Service.

                    중요 약관 변경은 시행 30일 전 사전 고지하며, 동의하지 않을 경우 서비스 이용을 중단할 수 있습니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>16. Miscellaneous / 기타</SectionTitle>
                <Paragraph>
                    Entire agreement: These Terms together with the Privacy Policy constitute the entire agreement between you and us.

                    Severability: If any provision is held invalid, the remaining provisions remain in full force.

                    No waiver: Failure to enforce any right is not a waiver of that right.

                    Language: These Terms are provided in English and Korean. In case of conflict, the Korean version prevails for users in Korea; the English version prevails for all other users.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>17. Contact / 연락처</SectionTitle>
                <Paragraph>
                    Company / 회사:
                    • Name: MindWorld (마인드월드)
                    • Representative: 윤우재
                    • Address: 경기도 의왕시 시청1로 11, Republic of Korea
                    • Business Registration: 504-43-28680
                    • Email: mind-wellness@naver.com

                    Response time: We aim to respond within 48 business hours.
                </Paragraph>
            </Section>

            <InfoBox>
                <SectionTitle>Document Information / 문서 정보</SectionTitle>
                <Paragraph>
                    • Originally published / 최초 작성일: 2026-10-13
                    • Last updated / 최종 수정일: 2026-10-13
                    • Effective / 시행일: 2026-10-13
                    • Version / 버전: 1.0
                </Paragraph>
            </InfoBox>
        </Container>
    );
};

export default CryptCrawlerTermsOfService;
